import { useEffect, useId, useState, type ChangeEvent } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import ClaimsFlowMain from './ClaimsFlowMain'
import ClaimsYellowHeader from './ClaimsYellowHeader'
import DashboardNavigation from './DashboardNavigation'
import Footer from './Footer'
import { ChevronRightIcon, FormCard, PlusIcon, PrimaryButton, SecondaryButton, YesNoToggle } from './ui'
import type { PolicyLink } from '../services/types'
import { decodePolicyRef } from '../utils/decodePolicyRef'
import ClaimsDesktopProgress from './ClaimsDesktopProgress'
import {
  buildClaimOtherGstHref,
  buildClaimOtherMultiVehicleDescribeHref,
  buildClaimOtherMultiVehicleDriverHref,
  formatClaimPolicySummaryLine,
  formatIncidentRailSummary,
  formatMultiVehicleCollisionRailSummary,
  formatMultiVehicleOtherPartyRailSummary,
  OTHER_CLAIM_SUBTYPE_QUERY_KEY,
} from '../claims/claimFlowModel'

import { parseOtherClaimSubtypeQuery, type OtherClaimSubtypeId } from '../claims/otherClaimSubtypeOptions'
import {
  persistMultiVehicleOtherPartyPatch,
  readClaimDraft,
} from '../claims/claimDraftStorage'
import {
  buildOtherPartyDriverPersistPatch,
  clearExtraOtherPartyDriversPatch,
  getExtraOtherDriverSlotCount,
  MAX_EXTRA_MULTI_VEHICLE_OTHER_DRIVERS,
  readOtherPartyDriverFromDraft,
  type MultiVehicleOtherPartyDriverFields,
  type MultiVehicleOtherPartyDriverIndex,
} from '../claims/multiVehicleOtherPartyDrivers'
import {
  filterAndTruncateRegistrationInput,
  REGISTRATION_DISPLAY_INPUT_MAX_LENGTH,
  REGISTRATION_NUMBER_MAX_LENGTH,
  sanitizeRegistrationNumber,
  validateOptionalRegistrationNumber,
} from '../utils/validation'
import { useEnsureOtherBranchGstAnswered } from '../claims/useEnsureOtherBranchGstAnswered'
import {
  CLAIM_FORM_FIELD_ERROR_RING_WRAPPER_CLASS,
  ClaimFormFieldError,
  ClaimFormSubmitSummaryAlert,
  useClaimFormSubmitAttempt,
} from '../claims/claimFormValidationFeedback'

const MULTI_VEHICLE_SUBTYPE = 'multi-vehicle-collision' as const satisfies OtherClaimSubtypeId

const EMPTY_DRIVER: MultiVehicleOtherPartyDriverFields = {
  name: '',
  address: '',
  phone: '',
  registration: '',
}

function OtherPartyDriverFields({
  driverIndex,
  fields,
  onChange,
  onRemove,
  inputClass,
  registrationError,
  registrationMaxLengthHint,
}: {
  driverIndex: MultiVehicleOtherPartyDriverIndex
  fields: MultiVehicleOtherPartyDriverFields
  onChange: (next: MultiVehicleOtherPartyDriverFields) => void
  onRemove?: () => void
  inputClass: string
  registrationError?: string
  registrationMaxLengthHint?: boolean
}) {
  const nameId = useId()
  const addressId = useId()
  const phoneId = useId()
  const registrationId = useId()
  const showHeading = driverIndex > 1

  const handleRegistrationChange = (e: ChangeEvent<HTMLInputElement>) => {
    onChange({ ...fields, registration: filterAndTruncateRegistrationInput(e.target.value) })
  }

  return (
    <div className="flex w-full flex-col gap-[16px]">
      {showHeading ? (
        <div className="flex items-center justify-between gap-[8px]">
          <h3 className="m-0 font-display font-semibold text-sm leading-sm text-text-primary-900">
            Additional driver {driverIndex}
          </h3>
          {onRemove ? (
            <SecondaryButton type="button" size="sm" onClick={onRemove}>
              Remove
            </SecondaryButton>
          ) : null}
        </div>
      ) : null}
      <label className="flex flex-col gap-[6px] font-body text-sm leading-sm text-text-tertiary-600">
        Name
        <input
          id={nameId}
          type="text"
          autoComplete="name"
          value={fields.name}
          onChange={(e) => onChange({ ...fields, name: e.target.value })}
          className={inputClass}
        />
      </label>
      <label className="flex flex-col gap-[6px] font-body text-sm leading-sm text-text-tertiary-600">
        Address
        <textarea
          id={addressId}
          rows={3}
          value={fields.address}
          onChange={(e) => onChange({ ...fields, address: e.target.value })}
          className={`${inputClass} min-h-[88px] resize-y`}
        />
      </label>
      <label className="flex flex-col gap-[6px] font-body text-sm leading-sm text-text-tertiary-600">
        Phone number
        <input
          id={phoneId}
          type="tel"
          autoComplete="tel"
          value={fields.phone}
          onChange={(e) => onChange({ ...fields, phone: e.target.value })}
          className={inputClass}
        />
      </label>
      <label className="flex flex-col gap-[6px] font-body text-sm leading-sm text-text-tertiary-600">
        Vehicle registration
        <input
          id={registrationId}
          type="text"
          autoComplete="off"
          value={fields.registration}
          onChange={handleRegistrationChange}
          maxLength={REGISTRATION_DISPLAY_INPUT_MAX_LENGTH}
          aria-invalid={registrationError ? true : undefined}
          aria-describedby={
            registrationError || registrationMaxLengthHint ? `${registrationId}-feedback` : undefined
          }
          className={
            registrationError
              ? `${inputClass} border-red-600 focus-visible:ring-red-600`
              : inputClass
          }
        />
        {registrationError ? (
          <ClaimFormFieldError id={`${registrationId}-feedback`}>{registrationError}</ClaimFormFieldError>
        ) : registrationMaxLengthHint ? (
          <p
            id={`${registrationId}-feedback`}
            className="m-0 font-body text-xs leading-xs text-text-tertiary-600"
          >
            Registration number cannot exceed {REGISTRATION_NUMBER_MAX_LENGTH} characters.
          </p>
        ) : null}
      </label>
    </div>
  )
}

/**
 * Multi-vehicle collision: optional details about the other involved driver (after insured driver step).
 */
export default function ClaimOtherMultiVehicleOtherPartyStep() {
  const navigate = useNavigate()
  const location = useLocation()
  const headingId = useId()

  const queryParams = new URLSearchParams(location.search)
  const policyRef = decodePolicyRef(queryParams.get('policyRef'))
  const typeInQuery = queryParams.get('type')
  const claimSubtype =
    parseOtherClaimSubtypeQuery(queryParams.get(OTHER_CLAIM_SUBTYPE_QUERY_KEY)) ??
    parseOtherClaimSubtypeQuery(readClaimDraft()?.otherClaimSubtypeId ?? null)
  const navState = location.state as { policy?: PolicyLink; preserveScroll?: boolean } | null

  const initialDraft = readClaimDraft()

  useEnsureOtherBranchGstAnswered({
    claimEntryBranch: 'other',
    policyRef,
    policyTypeQuery: typeInQuery,
    otherClaimSubtype: claimSubtype,
    navPolicy: navState?.policy,
  })

  const [hasDetails, setHasDetails] = useState<'Yes' | 'No' | null>(() => {
    const v = initialDraft?.multiVehicleOtherPartyDetailsKnown
    if (v === true) return 'Yes'
    if (v === false) return 'No'
    return null
  })
  const [driver1, setDriver1] = useState(() => readOtherPartyDriverFromDraft(initialDraft, 1))
  const [driver2, setDriver2] = useState(() => readOtherPartyDriverFromDraft(initialDraft, 2))
  const [driver3, setDriver3] = useState(() => readOtherPartyDriverFromDraft(initialDraft, 3))
  const [extraSlotCount, setExtraSlotCount] = useState<0 | 1 | 2>(() =>
    getExtraOtherDriverSlotCount(initialDraft)
  )
  const [registrationErrors, setRegistrationErrors] = useState<
    Partial<Record<MultiVehicleOtherPartyDriverIndex, string>>
  >({})
  const [registrationMaxLengthHint, setRegistrationMaxLengthHint] = useState<
    Partial<Record<MultiVehicleOtherPartyDriverIndex, boolean>>
  >({})

  useEffect(() => {
    if (!navState?.preserveScroll) {
      window.scrollTo(0, 0)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (claimSubtype !== MULTI_VEHICLE_SUBTYPE) {
      const path = buildClaimOtherGstHref(policyRef, typeInQuery, claimSubtype)
      navigate(path, {
        replace: true,
        state: navState?.policy ? { policy: navState.policy } : undefined,
      })
    }
  }, [claimSubtype, navigate, navState?.policy, policyRef, typeInQuery])

  const navigateWithPolicy = (path: string) => {
    navigate(path, {
      state: navState?.policy ? { policy: navState.policy } : undefined,
    })
  }

  const setDriverByIndex = (index: MultiVehicleOtherPartyDriverIndex, fields: MultiVehicleOtherPartyDriverFields) => {
    if (index === 1) setDriver1(fields)
    else if (index === 2) setDriver2(fields)
    else setDriver3(fields)
    const regoResult = validateOptionalRegistrationNumber(fields.registration)
    setRegistrationErrors((prev) => ({
      ...prev,
      [index]: regoResult.isValid ? undefined : regoResult.error,
    }))
    setRegistrationMaxLengthHint((prev) => ({
      ...prev,
      [index]:
        Boolean(fields.registration.trim()) &&
        sanitizeRegistrationNumber(fields.registration).length >= REGISTRATION_NUMBER_MAX_LENGTH,
    }))
  }

  const validateAllVisibleRegistrations = (): boolean => {
    const checks: Array<[MultiVehicleOtherPartyDriverIndex, MultiVehicleOtherPartyDriverFields]> = [[1, driver1]]
    if (extraSlotCount >= 1) checks.push([2, driver2])
    if (extraSlotCount >= 2) checks.push([3, driver3])
    let ok = true
    const nextErrors: Partial<Record<MultiVehicleOtherPartyDriverIndex, string>> = {}
    for (const [index, fields] of checks) {
      const result = validateOptionalRegistrationNumber(fields.registration)
      if (!result.isValid) {
        ok = false
        if (result.error) nextErrors[index] = result.error
      }
    }
    setRegistrationErrors(nextErrors)
    return ok
  }

  const persistDetailsKnown = (v: 'Yes' | 'No') => {
    clearSubmitAttempt()
    setRegistrationErrors({})
    setRegistrationMaxLengthHint({})
    setHasDetails(v)
    const known = v === 'Yes'
    persistMultiVehicleOtherPartyPatch(policyRef, {
      multiVehicleOtherPartyDetailsKnown: known,
      ...(known
        ? {
            multiVehicleOtherPartyDobDdMmYyyy: null,
            multiVehicleOtherPartyLicenceNumber: null,
          }
        : {
            multiVehicleOtherPartyName: null,
            multiVehicleOtherPartyAddress: null,
            multiVehicleOtherPartyDobDdMmYyyy: null,
            multiVehicleOtherPartyPhone: null,
            multiVehicleOtherPartyRegistration: null,
            multiVehicleOtherPartyLicenceNumber: null,
            ...clearExtraOtherPartyDriversPatch(),
          }),
    })
    if (!known) {
      setDriver1(EMPTY_DRIVER)
      setDriver2(EMPTY_DRIVER)
      setDriver3(EMPTY_DRIVER)
      setExtraSlotCount(0)
    }
  }

  const persistOptionalFields = () => {
    const patch = {
      ...buildOtherPartyDriverPersistPatch(1, driver1),
      ...clearExtraOtherPartyDriversPatch(),
      multiVehicleOtherPartyDobDdMmYyyy: null,
      multiVehicleOtherPartyLicenceNumber: null,
    }
    if (extraSlotCount >= 1) {
      Object.assign(patch, buildOtherPartyDriverPersistPatch(2, driver2))
    }
    if (extraSlotCount >= 2) {
      Object.assign(patch, buildOtherPartyDriverPersistPatch(3, driver3))
    }
    persistMultiVehicleOtherPartyPatch(policyRef, patch)
  }

  const backToDriverStep = () => {
    persistOptionalFields()
    if (claimSubtype === null) return
    navigateWithPolicy(buildClaimOtherMultiVehicleDriverHref(policyRef, typeInQuery, claimSubtype))
  }

  const handleNext = () => {
    if (hasDetails === 'Yes' && !validateAllVisibleRegistrations()) {
      return
    }
    attemptSubmit(() => {
      persistOptionalFields()
      if (claimSubtype === null) return
      navigateWithPolicy(buildClaimOtherMultiVehicleDescribeHref(policyRef, typeInQuery, claimSubtype))
    })
  }

  const handleAddDriver = () => {
    setExtraSlotCount((count) => (count < MAX_EXTRA_MULTI_VEHICLE_OTHER_DRIVERS ? ((count + 1) as 1 | 2) : count))
  }

  const handleRemoveDriver = (index: 2 | 3) => {
    if (index === 3) {
      setDriver3(EMPTY_DRIVER)
      setExtraSlotCount(1)
      persistMultiVehicleOtherPartyPatch(policyRef, {
        ...buildOtherPartyDriverPersistPatch(3, EMPTY_DRIVER),
      })
      return
    }
    if (extraSlotCount === 2) {
      setDriver2(driver3)
      setDriver3(EMPTY_DRIVER)
      setExtraSlotCount(1)
      persistMultiVehicleOtherPartyPatch(policyRef, {
        ...buildOtherPartyDriverPersistPatch(2, driver3),
        ...buildOtherPartyDriverPersistPatch(3, EMPTY_DRIVER),
      })
      return
    }
    setDriver2(EMPTY_DRIVER)
    setExtraSlotCount(0)
    persistMultiVehicleOtherPartyPatch(policyRef, {
      ...buildOtherPartyDriverPersistPatch(2, EMPTY_DRIVER),
      multiVehicleOtherParty3Name: null,
      multiVehicleOtherParty3Address: null,
      multiVehicleOtherParty3Phone: null,
      multiVehicleOtherParty3Registration: null,
    })
  }

  const draft = readClaimDraft()
  const policySummary = formatClaimPolicySummaryLine(
    navState?.policy ?? (policyRef ? { policyRef } : null)
  )

  const canProceed = hasDetails === 'Yes' || hasDetails === 'No'
  const { submitAttempted, showSubmitSummary, attemptSubmit, clearSubmitAttempt } =
    useClaimFormSubmitAttempt(canProceed)
  const hasDetailsError = submitAttempted && hasDetails === null
  const canAddAnotherDriver = extraSlotCount < MAX_EXTRA_MULTI_VEHICLE_OTHER_DRIVERS

  const inputClass =
    'w-full rounded-xl border border-solid border-border-primary bg-background-primary px-[14px] py-[12px] font-body text-sm leading-sm text-text-primary-900 shadow-xs placeholder:text-text-placeholder focus:outline-none focus-visible:ring-2 focus-visible:ring-foreground-brand-solid focus-visible:ring-offset-2'

  if (claimSubtype === null || claimSubtype !== MULTI_VEHICLE_SUBTYPE) {
    return null
  }

  return (
    <div className="relative flex min-h-screen w-full flex-col items-start bg-background-secondary">
      <DashboardNavigation />

      <ClaimsYellowHeader backAriaLabel="Back" onBack={backToDriverStep} title="Other driver" />

      <ClaimsFlowMain
        aside={
          <ClaimsDesktopProgress
            policySummary={policySummary}
            claimEntryBranch="other"
            otherClaimSubtypeId={claimSubtype}
            incidentSummary={formatIncidentRailSummary(draft)}
            multiVehicleCollisionSummary={formatMultiVehicleCollisionRailSummary(draft ?? undefined)}
            multiVehicleOtherPartySummary={formatMultiVehicleOtherPartyRailSummary(draft ?? undefined)}
            multiVehicleIncidentDescription={draft?.multiVehicleIncidentDescription ?? null}
            multiVehicleDamageDescription={draft?.multiVehicleDamageDescription ?? null}
            multiVehicleClaimDescription={draft?.multiVehicleClaimDescription ?? null}
            windscreenGstRegistered={draft?.windscreenGstRegistered ?? null}
            windscreenAbn={draft?.windscreenAbn ?? null}
            windscreenItcEntitled={draft?.windscreenItcEntitled ?? null}
            windscreenItcPercentage={draft?.windscreenItcPercentage ?? null}
          />
        }
      >
        <FormCard>
          <section className="flex w-full flex-col gap-[20px]" aria-labelledby={headingId}>
            {showSubmitSummary ? <ClaimFormSubmitSummaryAlert /> : null}
            <h2
              id={headingId}
              className="m-0 font-display font-semibold text-display-xs leading-display-xs text-text-primary-900"
            >
              Do you have the details of the other driver?
            </h2>
            <fieldset
              className={`m-0 w-full min-w-0 border-0 p-0 ${hasDetailsError ? CLAIM_FORM_FIELD_ERROR_RING_WRAPPER_CLASS : ''}`}
            >
              <legend className="sr-only">Choose Yes if you have details, or No.</legend>
              <YesNoToggle value={hasDetails ?? undefined} onChange={(v) => persistDetailsKnown(v)} />
            </fieldset>
            {hasDetailsError ? (
              <ClaimFormFieldError id={`${headingId}-error`}>Choose Yes or No to continue.</ClaimFormFieldError>
            ) : null}

            {hasDetails === 'Yes' ? (
              <div className="flex w-full flex-col gap-[20px]">
                <p className="m-0 font-body text-xs leading-xs text-text-tertiary-600">
                  These fields are optional. Provide whatever you can.
                </p>
                <OtherPartyDriverFields
                  driverIndex={1}
                  fields={driver1}
                  onChange={(next) => setDriverByIndex(1, next)}
                  inputClass={inputClass}
                  registrationError={registrationErrors[1]}
                  registrationMaxLengthHint={registrationMaxLengthHint[1]}
                />
                {extraSlotCount >= 1 ? (
                  <OtherPartyDriverFields
                    driverIndex={2}
                    fields={driver2}
                    onChange={(next) => setDriverByIndex(2, next)}
                    onRemove={() => handleRemoveDriver(2)}
                    inputClass={inputClass}
                    registrationError={registrationErrors[2]}
                    registrationMaxLengthHint={registrationMaxLengthHint[2]}
                  />
                ) : null}
                {extraSlotCount >= 2 ? (
                  <OtherPartyDriverFields
                    driverIndex={3}
                    fields={driver3}
                    onChange={(next) => setDriverByIndex(3, next)}
                    onRemove={() => handleRemoveDriver(3)}
                    inputClass={inputClass}
                    registrationError={registrationErrors[3]}
                    registrationMaxLengthHint={registrationMaxLengthHint[3]}
                  />
                ) : null}
                {canAddAnotherDriver ? (
                  <button
                    type="button"
                    onClick={handleAddDriver}
                    className="flex w-full items-center justify-center gap-[4px] overflow-clip rounded-md border border-solid border-border-primary bg-background-primary px-[12px] py-[8px] font-body text-sm font-semibold leading-sm text-text-secondary-700 shadow-xs"
                  >
                    <span className="size-5 shrink-0 text-text-tertiary-600" aria-hidden>
                      <PlusIcon />
                    </span>
                    Add another driver
                  </button>
                ) : null}
              </div>
            ) : null}
          </section>
        </FormCard>

        <div className="sticky bottom-0 w-full bg-background-secondary pb-[8px] pt-[8px] md:static md:py-0">
          <PrimaryButton
            type="button"
            onClick={handleNext}
            size="lg"
            fullWidth
            rightIcon={
              <div className="size-5 text-text-white">
                <ChevronRightIcon />
              </div>
            }
            aria-label="Next"
          >
            Next
          </PrimaryButton>
        </div>
      </ClaimsFlowMain>

      <Footer />
    </div>
  )
}
